import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faHammer } from '@fortawesome/free-solid-svg-icons/faHammer';
import '../../styles/nowWidget.css';

const CACHE_KEY = 'portfolio-now-activity';
const CACHE_TTL = 30 * 60 * 1000; // 30 min

const detectLang = () =>
  ((navigator.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en');

const COPY = {
  es: {
    open: 'Ver en qué ando',
    close: 'Cerrar',
    title: 'En qué ando',
    subtitle: 'El item activo de cada proyecto, en vivo desde sus issues.',
    empty: 'Nada marcado como activo ahora mismo.',
    error: 'No pude traer la actividad. Probá de nuevo en un rato.',
    loading: 'Cargando…',
    stale: 'Datos en caché (GitHub no respondió).',
    progress: 'Progreso del milestone',
    openIssues: 'abiertas',
    closedIssues: 'cerradas',
    totalIssues: 'issues',
  },
  en: {
    open: "See what I'm working on",
    close: 'Close',
    title: "What I'm working on",
    subtitle: "Each project's active item, live from its issues.",
    empty: 'Nothing marked active right now.',
    error: "Couldn't load activity. Try again in a bit.",
    loading: 'Loading…',
    stale: 'Cached data (GitHub did not respond).',
    progress: 'Milestone progress',
    openIssues: 'open',
    closedIssues: 'closed',
    totalIssues: 'issues',
  },
};

const STATUS_LABEL = {
  working: { es: 'en progreso', en: 'in progress' },
  planned: { es: 'planeado', en: 'planned' },
  idea: { es: 'idea', en: 'idea' },
  done: { es: 'hecho', en: 'done' },
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL) return null;
    return parsed.payload;
  } catch (error) {
    return null;
  }
};

const writeCache = (payload) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), payload }));
  } catch (error) {
    /* storage unavailable — cache is best effort */
  }
};

const NowWidget = () => {
  const lang = useRef(detectLang()).current;
  const t = COPY[lang];

  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState({
    status: 'idle',
    items: [],
    stale: false,
  });

  const fabRef = useRef(null);
  const closeRef = useRef(null);

  const load = useCallback(() => {
    const cached = readCache();
    if (cached) {
      setState({
        status: 'ready',
        items: cached.items || [],
        stale: Boolean(cached.stale),
      });
      return;
    }

    setState((prev) => (prev.status === 'ready' ? prev : { ...prev, status: 'loading' }));

    fetch('/.netlify/functions/activity')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        const payload = {
          items: data.items || [],
          stale: Boolean(data.stale),
        };
        writeCache(payload);
        setState({ status: 'ready', ...payload });
      })
      .catch(() => {
        setState((prev) =>
          prev.items.length ? prev : { status: 'error', items: [], stale: false }
        );
      });
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    load();
  }, [load]);

  const close = useCallback(() => {
    setIsOpen(false);
    fabRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, close]);

  return (
    <>
      <button
        ref={fabRef}
        type="button"
        className="now-widget__fab"
        onClick={open}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t.open}
        title={t.open}
      >
        <FontAwesomeIcon icon={faHammer} />
      </button>

      {isOpen ? (
        <div
          className="now-widget__overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t.title}
        >
          <div className="now-widget__panel">
            <header className="now-widget__header">
              <div>
                <h2 className="now-widget__title">{t.title}</h2>
                <p className="now-widget__subtitle">{t.subtitle}</p>
              </div>
              <button
                ref={closeRef}
                type="button"
                className="now-widget__close"
                onClick={close}
                aria-label={t.close}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>

            <div className="now-widget__body">
              {state.stale ? <p className="now-widget__notice">{t.stale}</p> : null}

              {state.status === 'loading' ? (
                <p className="now-widget__msg" aria-live="polite">
                  {t.loading}
                </p>
              ) : null}

              {state.status === 'error' ? (
                <p className="now-widget__msg">{t.error}</p>
              ) : null}

              {state.status === 'ready' && state.items.length === 0 ? (
                <p className="now-widget__msg">{t.empty}</p>
              ) : null}

              {state.items.length > 0 ? (
                <ul className="now-widget__list">
                  {state.items.map((item) => (
                    <li key={item.repo} className="now-widget__item">
                      <div className="now-widget__item-head">
                        <span className="now-widget__repo-group">
                          <span className="now-widget__repo">{item.name}</span>
                          {item.currentVersion ? (
                            <span className="now-widget__version-current">
                              {item.currentVersion}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={`now-widget__pill now-widget__pill--${item.status}${
                            item.isNow ? ' now-widget__pill--now' : ''
                          }`}
                        >
                          {item.isNow
                            ? lang === 'es'
                              ? 'ahora'
                              : 'now'
                            : (STATUS_LABEL[item.status] || {})[lang] || item.status}
                        </span>
                      </div>
                      <p className="now-widget__item-title">
                        {(item.milestoneStats && item.milestoneStats.title) || item.title}
                      </p>
                      {item.milestoneStats ? (
                        <div
                          className="now-widget__progress now-widget__progress--inline"
                          role="group"
                          aria-label={`${t.progress}: ${item.milestoneStats.title}`}
                        >
                          <div className="now-widget__progress-head">
                            <p className="now-widget__progress-label">{t.progress}</p>
                            <strong className="now-widget__progress-value">
                              {item.milestoneStats.progress}%
                            </strong>
                          </div>
                          <div
                            className="now-widget__progress-track"
                            role="progressbar"
                            aria-label={item.milestoneStats.title}
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-valuenow={item.milestoneStats.progress}
                          >
                            <span
                              className="now-widget__progress-fill"
                              style={{ width: `${item.milestoneStats.progress}%` }}
                            />
                          </div>
                          <p className="now-widget__progress-meta">
                            {item.milestoneStats.closedIssues} {t.closedIssues} ·{' '}
                            {item.milestoneStats.openIssues} {t.openIssues} ·{' '}
                            {item.milestoneStats.totalIssues} {t.totalIssues}
                          </p>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default NowWidget;
